import type Pusher from 'pusher-js/types/src/core/pusher';
import React, { type PropsWithChildren, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { updateConnectionState } from '../slices/chatConnection/chatConnectionSlice';
import type { ChannelName, PusherChannelEvent, PusherConnectionState } from './pusher-constants';

type PusherProviderProps = {
  pusher: Pusher;
};

export type EventConfig = {
  channelName: ChannelName;
  eventName: PusherChannelEvent;
  // any for now until we use proper types from the BE
  // biome-ignore lint/suspicious/noExplicitAny: <explanation>
  callback: (data: any) => void;
};

type PusherContextType = {
  pusher: Pusher;
  addEventConfig: (config: EventConfig) => void;
  removeEventConfig: (config: EventConfig) => void;
};

export const getPusherEventKey = (config: EventConfig) => `${config.channelName}:${config.eventName}`;

const PusherContext = React.createContext<PusherContextType | undefined>(undefined);

export function PusherProvider({ pusher, children }: PropsWithChildren<PusherProviderProps>) {
  const dispatch = useDispatch();
  const eventConfigsRef = useRef<Map<string, EventConfig>>(new Map());

  // Update Redux state on connection state change
  useEffect(() => {
    const handleStateChange = (states: { previous: PusherConnectionState; current: PusherConnectionState }) => {
      dispatch(updateConnectionState({ connectionState: states.current }));
    };

    pusher.connection.bind('state_change', handleStateChange);

    return () => {
      pusher.connection.unbind('state_change', handleStateChange);
    };
  }, [dispatch, pusher]);

  // Bind a new event
  const bindEvent = (config: EventConfig) => {
    let channel = pusher.channel(config.channelName);
    if (!channel) {
      channel = pusher.subscribe(config.channelName);
    }
    channel.bind(config.eventName, config.callback);
  };

  // Unbind an event
  const unbindEvent = (config: EventConfig) => {
    const channel = pusher.channel(config.channelName);
    if (channel) {
      channel.unbind(config.eventName, config.callback);
    }
  };

  // Add a new event configuration
  const addEventConfig = (config: EventConfig) => {
    const key = getPusherEventKey(config);

    // Only bind if the event isn’t already configured
    if (!eventConfigsRef.current.has(key)) {
      bindEvent(config);
      eventConfigsRef.current.set(key, config);
    }
  };

  // Remove an existing event configuration
  const removeEventConfig = (config: EventConfig) => {
    const key = getPusherEventKey(config);

    // Unbind and remove from the map if it exists
    if (eventConfigsRef.current.has(key)) {
      unbindEvent(config);
      eventConfigsRef.current.delete(key);
    }
  };

  return (
    <PusherContext.Provider value={{ pusher, addEventConfig, removeEventConfig }}>{children}</PusherContext.Provider>
  );
}

export function usePusher() {
  const pusher = React.useContext(PusherContext);

  if (!pusher) {
    throw new Error('usePusher must be used within a PusherProvider');
  }

  return pusher;
}
